import React, { useState } from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentIcon from "@mui/icons-material/Assignment";

// Animation variants
const buttonVariants = {
  rest: { scale: 1 },
  hover: { scale: 1.1 },
  tap: { scale: 0.95 },
};

export default function DriverRow({
  driver,
  _handleSetDriverOrderModal,
  toggleCreateEditModal,
  deleteDriver,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    // Make sure to stop propagation to prevent row click
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (e) => {
    e.stopPropagation(); // Prevent row click
    handleMenuClose();
    toggleCreateEditModal(e, driver.driver_id);
  };

  const handleDelete = (e) => {
    e.stopPropagation(); // Prevent row click
    handleMenuClose();
    deleteDriver(driver);
  };

  const handleSelectDriver = () => {
    _handleSetDriverOrderModal(driver);
  };

  // Generate a random pastel color based on the driver's name for the avatar
  const getAvatarColor = (name) => {
    const hash = name
      .split("")
      .reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 80%)`;
  };

  return (
    <motion.div
      whileHover={{ backgroundColor: "rgba(0,0,0,0.03)" }}
      transition={{ duration: 0.2 }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={handleSelectDriver}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <Avatar
            sx={{
              bgcolor: getAvatarColor(driver.name),
              color: "rgba(0,0,0,0.7)",
              mr: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {driver.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {driver.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
              <Chip
                label={`ID: ${driver.easy_id}`}
                size="small"
                color="primary"
                variant="outlined"
                icon={
                  <LocalShippingIcon sx={{ fontSize: "0.8rem !important" }} />
                }
                sx={{ mr: 1, height: 24 }}
              />
              {driver.active !== undefined && (
                <Chip
                  label={driver.active ? "Ativo" : "Inativo"}
                  size="small"
                  color={driver.active ? "success" : "default"}
                  sx={{ height: 24 }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <motion.div
          variants={buttonVariants}
          initial="rest"
          whileHover="hover"
          whileTap="tap"
        >
          <IconButton
            aria-label="driver-options"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClick(e);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </motion.div>

        <Menu
          id="driver-options-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          sx={{ zIndex: 9999 }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1,
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.12))",
              borderRadius: 2,
              minWidth: 180,
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
        >
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleSelectDriver}>
            <ListItemIcon>
              <AssignmentIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText>Selecionar</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteForeverIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Excluir</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </motion.div>
  );
}
